/**
 * The ratification surface (rulings 2026-08-06, vault "Ratification
 * Surface"): the SOURCE TEXT is the protagonist -- the section with
 * its quoted candidate highlighted at the line anchor -- and the form
 * completes the section into a Rule. The laws built in:
 *  - confirm-the-quote: the threshold value fills ONLY when the human
 *    clicks the highlighted quote, never silently;
 *  - typed-by-construction domain: payload keys come from a reference
 *    project's OBSERVED vocabulary (a typo'd key is unrepresentable);
 *    values are offered from observed values but stay free (a value
 *    the corpus lacks can still be law -- zero matches warns, never
 *    blocks);
 *  - server-truth evidence: the match preview renders honest ABSENCE
 *    until the backend's domain-preview endpoint exists -- a
 *    client-side reimplementation of selector semantics would let the
 *    evidence itself lie;
 *  - the receipt: success shows the standing fact it minted (name,
 *    WHO, and suggested-vs-ratified -- the drift record, visible).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
} from 'antd';
import { ACCENT, INK, tint } from '../../../theme';
import { AbsenceBadge, ApprovalMark, SuggestionNote } from '../../../strata';
import {
  useDomainPreview,
  useProjectOptions,
  useProjectVocabulary,
  useRatifySection,
  useRuleSections,
  useSectionSuggestion,
  type RatifiedRule,
  type RuleDomain,
  type RuleSourceSection,
} from '../queries';

const UNITS = ['m', 'pct', 'lux', 'count'];
const CATEGORIES = ['ELECTRICAL', 'PLUMBING', 'MECHANICAL', 'ARCHITECTURAL'];

/** The section text with the quote highlighted where it stands --
 * the human's eyes belong on the source, not a detached number. */
function SourceText({ text, quote }: { text: string; quote: string | null }) {
  const parts = useMemo(() => {
    if (!quote) return null;
    const at = text.indexOf(quote);
    if (at === -1) return null;
    return [text.slice(0, at), text.slice(at, at + quote.length), text.slice(at + quote.length)];
  }, [text, quote]);
  return (
    <pre
      style={{
        whiteSpace: 'pre-wrap',
        fontFamily: 'inherit',
        fontSize: 13,
        lineHeight: 1.6,
        margin: 0,
        color: INK.primary,
      }}
    >
      {parts ? (
        <>
          {parts[0]}
          <mark style={{ background: tint(ACCENT.amber, 0.35), color: ACCENT.amber, padding: '0 2px' }}>
            {parts[1]}
          </mark>
          {parts[2]}
        </>
      ) : (
        text
      )}
    </pre>
  );
}

function Receipt({ result }: { result: RatifiedRule }) {
  const ratified = result.rule as { threshold?: { value?: number; unit?: string } };
  const drifted =
    result.suggested !== null &&
    ratified.threshold?.value !== undefined &&
    ratified.threshold.value !== result.suggested.value;
  return (
    <Card size="small" title={`Rule born: ${result.name}`}>
      <Space orientation="vertical" size="small">
        <ApprovalMark decision="ratified" who={result.ratified_by} />
        {result.suggested && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            suggested “{result.suggested.quote}” → ratified{' '}
            {ratified.threshold?.value} {ratified.threshold?.unit}
            {drifted && ' (differs from the quote — recorded)'}
          </Typography.Text>
        )}
        <Link to="/rules" search={{ tab: 'ruleset', name: result.name }}>
          View it standing in the ruleset →
        </Link>
      </Space>
    </Card>
  );
}

export function RatifySectionPanel({ sectionId }: { sectionId: string }) {
  const sections = useRuleSections();
  const [measurement, setMeasurement] = useState<string | null>(null);
  const suggestion = useSectionSuggestion(sectionId, measurement);
  const ratify = useRatifySection(sectionId);

  // The form (numbers enter HERE and nowhere else)
  const [op, setOp] = useState<'gt' | 'lt'>('gt');
  const [value, setValue] = useState<number | null>(null);
  const [unit, setUnit] = useState<string | null>(null);
  const [jurisdiction, setJurisdiction] = useState('US');
  const [summary, setSummary] = useState('');
  const [severity, setSeverity] = useState<'violation' | 'warning' | 'advisory'>('violation');
  const [noData, setNoData] = useState<'rest' | 'attend' | 'block'>('attend');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [payloadRows, setPayloadRows] = useState<{ key: string | null; value: string }[]>([]);

  // The observed vocabulary's reference corpus
  const { data: projects = [] } = useProjectOptions();
  const [vocabProject, setVocabProject] = useState<string | null>(null);
  const vocabulary = useProjectVocabulary(vocabProject);

  // Server-truth evidence against the picked reference corpus (the
  // domain is rebuilt from form state so every edit refetches).
  const domainPayload = Object.fromEntries(
    payloadRows.filter((r) => r.key && r.value !== '').map((r) => [r.key!, r.value]),
  );
  const domain: RuleDomain = { category, payload: domainPayload, payload_regex: {} };
  const preview = useDomainPreview(vocabProject, domain);

  const section: RuleSourceSection | null =
    sections.data?.backendPending === false
      ? sections.data.sections.find((s) => s.id === sectionId) ?? null
      : null;

  // The section's classification is a STANDING LABEL (a fact), so the
  // measurement may start there -- the bias bundle forbids auto-filled
  // NUMBERS, never facts. The human can still change it.
  useEffect(() => {
    if (measurement === null && section?.classification) {
      setMeasurement(section.classification);
    }
  }, [measurement, section?.classification]);

  if (sections.data?.backendPending) {
    return <Alert type="warning" showIcon title="The rules backend is pending — sections are not readable yet." />;
  }
  if (!section) {
    return <Typography.Text type="secondary">Loading the section…</Typography.Text>;
  }

  const quote = suggestion.data?.suggestion ?? null;
  const measurements = suggestion.data?.measurements ?? [];
  const canSubmit =
    measurement !== null &&
    value !== null &&
    unit !== null &&
    summary.trim() !== '' &&
    !ratify.isPending;

  const submit = () =>
    ratify.mutate({
      name: name.trim(),
      jurisdiction: jurisdiction.trim(),
      summary: summary.trim(),
      measurement: measurement!,
      threshold: { op, value: value!, unit: unit! },
      domain,
      severity,
      no_data: noData,
    });

  if (ratify.data) return <Receipt result={ratify.data} />;

  return (
    <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* The protagonist: the source */}
      <Card
        size="small"
        style={{ flex: '1 1 420px', minWidth: 360 }}
        title={`${section.code} ${section.edition} §${section.section} — ${section.title}`}
      >
        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {section.source_file} · lines {section.line_start}–{section.line_end}
          </Typography.Text>
          {section.verified_by === null ? (
            <AbsenceBadge
              label="UNVERIFIED"
              reason="scrape-grade sections must be checked against the authoritative source before ratification (the server enforces this)"
            />
          ) : (
            <ApprovalMark decision="verified" who={section.verified_by} />
          )}
          <SourceText text={section.text} quote={quote?.quote ?? null} />
          {quote && (
            <Button
              size="small"
              onClick={() => {
                setValue(quote.value);
                setUnit(quote.unit);
              }}
            >
              Use the quoted “{quote.quote}” ({quote.value} {quote.unit})
            </Button>
          )}
          {measurement && suggestion.isSuccess && !quote && (
            <SuggestionNote
              kind="advisory"
              description="No unambiguous quote for this measurement — the book prints no single value in its unit, so the form starts blank (ambiguity refuses to guess)."
            />
          )}
        </Space>
      </Card>

      {/* The completion */}
      <Card size="small" style={{ flex: '1 1 380px', minWidth: 340 }} title="Complete into a Rule">
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Select
            placeholder="Measurement (the closed registry)"
            value={measurement}
            onChange={(m) => {
              setMeasurement(m);
              setValue(null);
              setUnit(null);
            }}
            options={measurements.map((m) => ({ value: m, label: m }))}
            style={{ width: '100%' }}
          />
          <Space wrap>
            <Select
              value={op}
              onChange={setOp}
              options={[
                { value: 'gt', label: 'must be >' },
                { value: 'lt', label: 'must be <' },
              ]}
              style={{ width: 120 }}
            />
            <InputNumber
              placeholder="value"
              value={value}
              onChange={setValue}
              aria-label="Threshold value"
              style={{ width: 120 }}
            />
            <Select
              placeholder="unit"
              value={unit}
              onChange={setUnit}
              options={UNITS.map((u) => ({ value: u, label: u }))}
              style={{ width: 90 }}
            />
          </Space>

          <Input.TextArea
            placeholder="Summary — YOUR paraphrase of the provision (copied code text is refused: the citation carries the book's words, the summary carries yours)"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={3}
          />

          <Space wrap>
            <Input
              placeholder="jurisdiction"
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              style={{ width: 110 }}
            />
            <Select
              value={severity}
              onChange={setSeverity}
              options={['violation', 'warning', 'advisory'].map((s) => ({ value: s, label: s }))}
              style={{ width: 120 }}
            />
            <Select
              value={noData}
              onChange={setNoData}
              options={[
                { value: 'attend', label: 'no data → attend' },
                { value: 'rest', label: 'no data → rest' },
                { value: 'block', label: 'no data → block' },
              ]}
              style={{ width: 170 }}
            />
          </Space>
          <Input
            placeholder="name (blank = derived from code+section)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          {/* The domain: typed by construction */}
          <Card size="small" type="inner" title="Domain — which elements this rule quantifies over">
            <Space orientation="vertical" size="small" style={{ width: '100%' }}>
              <Select
                placeholder="Reference project (supplies the observed vocabulary)"
                value={vocabProject}
                onChange={setVocabProject}
                options={projects.map((p) => ({ value: p, label: p }))}
                style={{ width: '100%' }}
                showSearch
              />
              <Select
                placeholder="Category constraint (optional)"
                allowClear
                value={category}
                onChange={(c) => setCategory(c ?? null)}
                options={CATEGORIES.map((c) => ({ value: c, label: c }))}
                style={{ width: '100%' }}
              />
              {payloadRows.map((row, i) => (
                <Space key={i} wrap>
                  <Select
                    placeholder="payload key (observed)"
                    value={row.key}
                    onChange={(key) =>
                      setPayloadRows((rows) => rows.map((r, j) => (j === i ? { ...r, key } : r)))
                    }
                    options={(vocabulary.data?.keys ?? []).map((k) => ({ value: k, label: k }))}
                    style={{ width: 200 }}
                    showSearch
                    disabled={!vocabulary.data}
                  />
                  <AutoComplete
                    placeholder="value"
                    value={row.value}
                    onChange={(v) =>
                      setPayloadRows((rows) =>
                        rows.map((r, j) => (j === i ? { ...r, value: v } : r)),
                      )
                    }
                    options={(row.key ? vocabulary.data?.values[row.key] ?? [] : []).map((v) => ({
                      value: v,
                    }))}
                    style={{ width: 200 }}
                  />
                  <Button
                    size="small"
                    onClick={() => setPayloadRows((rows) => rows.filter((_, j) => j !== i))}
                  >
                    remove
                  </Button>
                </Space>
              ))}
              <Button
                size="small"
                disabled={!vocabulary.data}
                onClick={() => setPayloadRows((rows) => [...rows, { key: null, value: '' }])}
              >
                + condition
              </Button>
              {!vocabProject && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Pick a reference project first — payload keys are only ever chosen from a
                  corpus's OBSERVED vocabulary (a typo'd key is unrepresentable).
                </Typography.Text>
              )}
              {/* Evidence: server truth or honest absence -- never a client guess */}
              {vocabProject && preview.data?.backendPending === false && (
                <div style={{ fontSize: 12 }}>
                  {preview.data.match_count === 0 ? (
                    <AbsenceBadge
                      label="MATCHES NOTHING"
                      reason={`This domain selects zero elements in ${vocabProject}. Ratification stays possible — a rule can be valid law for elements this corpus lacks.`}
                    />
                  ) : (
                    <>
                      <Typography.Text style={{ fontSize: 12 }}>
                        in {vocabProject}: <b>{preview.data.match_count}</b> elements
                      </Typography.Text>
                      <div style={{ color: INK.secondary, marginTop: 4 }}>
                        {preview.data.sample
                          .map((m) => m.element_name ?? m.origin_id)
                          .slice(0, 6)
                          .join(' · ')}
                        {preview.data.match_count > 6 && ' · …'}
                      </div>
                    </>
                  )}
                </div>
              )}
              {vocabProject && preview.data?.backendPending && (
                <AbsenceBadge
                  label="PREVIEW UNAVAILABLE"
                  reason="This API build predates the domain-preview endpoint — restart/rebuild the api container. The panel never reimplements selector semantics client-side."
                />
              )}
            </Space>
          </Card>

          {ratify.isError && <Alert type="error" showIcon title={ratify.error.message} />}
          <Button type="primary" disabled={!canSubmit} loading={ratify.isPending} onClick={submit}>
            Ratify
          </Button>
        </Space>
      </Card>
    </div>
  );
}
